/**
 * Order-form price validation UI (Tradesea "Price not in multiple of…", SPEC §9.2 Order mode).
 * Validation only; nothing here submits anything. The gateway re-validates (§9.9).
 */

export type PriceCheck = { ok: true; price: number } | { ok: false; message: string };

/** Decimal places needed to print a tick size exactly (0.25 → 2, 0.1 → 1, 1 → 0). */
export function decimalsOf(tick: number): number {
  for (let d = 0; d <= 8; d++) {
    if (Math.abs(Math.round(tick * 10 ** d) - tick * 10 ** d) < 1e-9) return d;
  }
  return 8;
}

/**
 * Parse a user-typed price and check it sits on the tick grid.
 * Accepts "21,450.25" (thousands separators) and surrounding whitespace.
 */
export function checkPrice(input: string, tick: number): PriceCheck {
  const s = input.trim().replace(/,/g, "");
  if (s === "") return { ok: false, message: "Enter a price" };
  if (!/^-?\d+(\.\d+)?$/.test(s)) return { ok: false, message: "Price must be a number" };
  const price = Number(s);
  if (!(tick > 0)) return { ok: true, price };
  if (price <= 0) return { ok: false, message: "Price must be above zero" };
  const steps = price / tick;
  // Tolerance scaled to the magnitude so 21450.25 / 0.25 does not trip on float error.
  if (Math.abs(steps - Math.round(steps)) > 1e-7 * Math.max(1, Math.abs(steps))) {
    return { ok: false, message: `Price not in multiple of ${tick.toFixed(decimalsOf(tick))}` };
  }
  return { ok: true, price };
}

/** Step a price by n ticks, snapped to the grid and rounded to the tick's decimals. */
export function stepPrice(price: number, n: number, tick: number): number {
  const d = decimalsOf(tick);
  return Number(((Math.round(price / tick) + n) * tick).toFixed(d));
}

/** Clamp a quantity to 1..max as an integer (SPEC §9.6). */
export function clampQty(q: number, max: number): number {
  if (!Number.isFinite(q)) return 1;
  return Math.min(Math.max(1, Math.trunc(q)), Math.max(1, max));
}

/** One edit of the typed quantity: what the field shows, and the quantity to use now (if any). */
export type QtyEdit = { draft: string; commit: number | null };

/**
 * A keystroke in the quantity field (OB4). The field may be empty while editing: backspacing "1"
 * and typing "3" must give 3, never 13 (an empty field used to snap back to 1 and the next digit
 * was appended to it). An empty or zero draft commits nothing, so the order quantity keeps its
 * previous value until a real number is typed; a whole number ≥ 1 commits at once, so a click that
 * trades without taking focus from the field never uses a stale quantity.
 */
export function editQty(input: string, max: number): QtyEdit {
  const draft = input.replace(/\D/g, "");
  const n = Number(draft);
  if (draft === "" || !Number.isSafeInteger(n) || n < 1) return { draft, commit: null };
  const q = clampQty(n, max);
  return { draft: q === n ? draft : String(q), commit: q };
}

/** Leaving the field (blur / Enter): an empty or unusable draft restores `previous`, never 1. */
export function settleQty(draft: string, previous: number, max: number): number {
  return editQty(draft, max).commit ?? previous;
}

export type TicksCheck = { ok: true; ticks: number } | { ok: false; message: string };

/**
 * A user-typed price → integer ticks, exactly (SPEC §1.3 "integer ticks for all internal price
 * math"): the decimal string is read as an integer over a power of ten and must be a whole number
 * of ticks of `tickNum / tickDen` — no floating point is involved. "21,450.25" with 1/4 → 85801.
 */
export function priceToTicks(input: string, tickNum: number, tickDen: number): TicksCheck {
  const s = input.trim().replace(/,/g, "");
  if (s === "") return { ok: false, message: "Enter a price" };
  const m = /^(\d+)(?:\.(\d+))?$/.exec(s);
  if (!m) return { ok: false, message: "Price must be a positive number" };
  if (!(tickNum > 0) || !(tickDen > 0) || !Number.isInteger(tickNum) || !Number.isInteger(tickDen)) {
    return { ok: false, message: "Tick size unknown" };
  }
  const frac = m[2] ?? "";
  const scaled = BigInt(`${m[1] ?? "0"}${frac}`); // price × 10^frac.length
  const scale = 10n ** BigInt(frac.length);
  // ticks = price / (num/den) = scaled × den / (scale × num)
  const top = scaled * BigInt(tickDen);
  const bottom = scale * BigInt(tickNum);
  if (top % bottom !== 0n) {
    return { ok: false, message: `Price not in multiple of ${String(tickNum / tickDen)}` };
  }
  const ticks = top / bottom;
  if (ticks <= 0n) return { ok: false, message: "Price must be above zero" };
  if (ticks > BigInt(Number.MAX_SAFE_INTEGER)) return { ok: false, message: "Price out of range" };
  return { ok: true, ticks: Number(ticks) };
}
